import type {
  ConversationTriageRunActionRaw,
  ConversationTriageRunRaw,
  ConversationTriageRunStepRaw,
} from 'src/types';

// Mirrors `@provision/entity-types` `TriageActionVisibility` (same string values).
// Declared locally because the portal consumes entity-types as types only — its
// CommonJS barrel does not expose runtime enum values to Vite.
export enum TriageActionVisibility {
  AutoApply = 'auto_apply',
  Show = 'show',
  Hide = 'hide',
}

export enum TriageActionStatus {
  Suggested = 'suggested',
  InProgress = 'in_progress',
  Applied = 'applied',
  Failed = 'failed',
  Dismissed = 'dismissed',
}

export enum TriageRunStepStatus {
  InProgress = 'in_progress',
  Resolved = 'resolved',
  Error = 'error',
  Superseded = 'superseded',
}

export enum TriageActionResolution {
  Execute = 'EXECUTE',
  Dismiss = 'DISMISS',
}

export enum TriageRunProgress {
  Running = 'running',
  Complete = 'complete',
  Failed = 'failed',
  Superseded = 'superseded',
}

export enum TriageActionGroup {
  Applied = 'applied',
  Suggested = 'suggested',
  Failed = 'failed',
  Dismissed = 'dismissed',
}

export type TriageActionAccent =
  | 'urgency'
  | 'status'
  | 'type'
  | 'group'
  | 'assignee'
  | 'default';

export interface TriageActionDisplay {
  label: string;
}

export type TriageRunActionRaw = Pick<
  ConversationTriageRunActionRaw,
  'id' | 'conversationTriageRunActionId' | 'status' | 'visibility' | 'payload'
> & {
  // Nullable at runtime even though the generated entity types them as `string`.
  display?: string | null;
  appliedAt?: string | null;
  appliedBy?: string | null;
  applyError?: string | null;
  createdAt?: string | null;
};

export type TriageRunStepRaw = Pick<
  ConversationTriageRunStepRaw,
  'id' | 'conversationTriageRunStepId' | 'status' | 'createdAt'
> & {
  conversationTriageRunActions?: TriageRunActionRaw[] | null;
};

export type TriageRunRaw = Pick<
  ConversationTriageRunRaw,
  | 'id'
  | 'conversationTriageRunId'
  | 'conversationId'
  | 'partnerId'
  | 'userInstructions'
  | 'createdAt'
> & {
  conversationTriageRunSteps?: TriageRunStepRaw[] | null;
};

export interface TriageAttempt {
  id: string;
  status: TriageActionStatus;
  applyError: string | null;
  timestamp: string;
}

export interface TriageAction {
  id: string;
  group: TriageActionGroup;
  status: TriageActionStatus;
  label: string;
  hasLabel: boolean;
  reason: string | null;
  icon: string;
  accent: TriageActionAccent;
  color?: string;
  applyError: string | null;
  attempts: number;
  history: TriageAttempt[];
}

export interface TriageRunGroups {
  applied: TriageAction[];
  suggested: TriageAction[];
  failed: TriageAction[];
  dismissed: TriageAction[];
}

export interface TriageRun {
  id: string;
  conversationId: string;
  timestamp: string;
  progress: TriageRunProgress;
  groups: TriageRunGroups;
  pendingSuggestedCount: number;
}
